import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Trash2, CalendarDays, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import ConfirmationMessageModal from "@/pages/dashboard/ConfirmationMessageModal";

const toISO = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromISO = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const niceDate = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "");
const ITEM_CATS = ["Food/Catering", "Decoration", "DJ/Music", "Photography", "Other"];

export default function BookDateModal({ open, onClose, venues, businessId, defaultVenueId, defaultDate, editing, currentUserName, onSaved }) {
  const empty = { venue_id: "", date: "", booker_name: "", client_name: "", client_phone: "", amount: "", note: "", status: "confirmed", allow_multiple: false, payment_mode: "", payment_type: "full", advance_amount: "", amount_online: "", amount_cash: "", items: [] };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [msgBookingId, setMsgBookingId] = useState(null);
  const [msgMode, setMsgMode] = useState(null); // "created" | "edit"
  const dash = useDash();
  const canDelete = dash?.can?.delete !== false;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        venue_id: editing.venue_id, date: editing.date, booker_name: editing.booker_name || "",
        client_name: editing.client_name || "", client_phone: editing.client_phone || "",
        amount: editing.amount ?? "", note: editing.note || "",
        status: editing.status || "confirmed", allow_multiple: true,
        payment_mode: editing.payment_mode || "", payment_type: editing.payment_type || "full",
        advance_amount: editing.advance_received ?? "",
        amount_online: editing.amount_online ?? "", amount_cash: editing.amount_cash ?? "",
        items: Array.isArray(editing.items) ? editing.items.map((it) => ({ category: it.category || "Other", label: it.label || "", amount: it.amount ?? "", cost: it.cost ?? "" })) : [],
      });
    } else {
      setForm({ ...empty, venue_id: defaultVenueId || venues[0]?.venue_id || "", date: defaultDate || "", booker_name: currentUserName || "" });
    }
    setSuccess(false);
    setDateOpen(false);
    // Re-init when the modal opens OR when the target date / editing target changes.
    // (defaultVenueId is intentionally excluded — a venue refetch must not blank the form;
    //  the self-heal effect below restores the venue instead.)
    // eslint-disable-next-line
  }, [open, defaultDate, editing]);

  // Self-heal: if the venue list wasn't ready when the modal opened, fill it in as soon as it arrives.
  useEffect(() => {
    if (!open || editing) return;
    const vid = defaultVenueId || venues[0]?.venue_id;
    if (!vid) return;
    setForm((f) => (f.venue_id ? f : { ...f, venue_id: vid }));
    // eslint-disable-next-line
  }, [open, editing, defaultVenueId, venues]);

  const submit = async (e) => {
    e.preventDefault();
    const venueId = form.venue_id || defaultVenueId || venues[0]?.venue_id || "";
    if (!venueId || !form.date) {
      toast.error("Please pick a venue and date.");
      return;
    }
    if (!form.client_name.trim()) {
      toast.error("Client name is required.");
      return;
    }
    if (!form.payment_mode) {
      toast.error("Please select how the payment was received (Online, Cash or Split).");
      return;
    }
    const ao = parseFloat(form.amount_online) || 0;
    const ac = parseFloat(form.amount_cash) || 0;
    const splitSum = ao + ac;
    const totalVal = parseFloat(form.amount) || 0;
    const isAdvance = form.payment_type === "advance";
    const advReceived = form.payment_mode === "split" ? splitSum : (parseFloat(form.advance_amount) || 0);
    if (isAdvance) {
      if (!(totalVal > 0)) { toast.error("Enter the total amount."); return; }
      if (advReceived <= 0) { toast.error("Enter the advance amount received."); return; }
      if (advReceived > totalVal) { toast.error("Advance can't be more than the total."); return; }
    } else if (form.payment_mode === "split") {
      if (splitSum <= 0) { toast.error("Enter the online and/or cash amount."); return; }
    } else if (form.amount === "" || form.amount === null || isNaN(totalVal)) {
      toast.error("Amount is required."); return;
    }
    setSaving(true);
    const params = businessId ? { business_id: businessId } : {};
    const cleanItems = (form.items || [])
      .map((it) => ({ category: it.category || "Other", label: (it.label || "").trim(), amount: parseFloat(it.amount) || 0, cost: parseFloat(it.cost) || 0 }))
      .filter((it) => it.label || it.amount > 0 || it.cost > 0);
    const payload = {
      venue_id: venueId, date: form.date, booker_name: form.booker_name.trim(),
      client_name: form.client_name.trim(), client_phone: form.client_phone.trim() || undefined,
      amount: isAdvance ? totalVal : (form.payment_mode === "split" ? splitSum : totalVal),
      payment_mode: form.payment_mode, payment_type: form.payment_type,
      advance_amount: isAdvance && form.payment_mode !== "split" ? advReceived : undefined,
      amount_online: form.payment_mode === "split" ? ao : undefined,
      amount_cash: form.payment_mode === "split" ? ac : undefined,
      items: cleanItems,
      note: form.note.trim() || null, status: form.status, allow_multiple: form.allow_multiple,
    };
    try {
      if (editing) {
        await api.put(`/bookings/${editing.booking_id}`, payload, { params });
        toast.success("Booking updated");
        onSaved?.();
        onClose();
      } else {
        const res = await api.post("/bookings", payload, { params });
        setSuccess(true);
        setTimeout(() => { onSaved?.(); setMsgMode("created"); setMsgBookingId(res.data.booking_id); }, 1100);
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(err.response.data.detail || "A booking already exists for this day.");
      } else {
        toast.error("Could not save booking.");
      }
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Cancel this booking? Revenue will be adjusted.")) return;
    try {
      await api.delete(`/bookings/${editing.booking_id}`, { params: businessId ? { business_id: businessId } : {} });
      toast.success("Booking cancelled");
      onSaved?.();
      onClose();
    } catch { toast.error("Could not cancel booking."); }
  };

  const closeMsg = () => {
    const wasCreated = msgMode === "created";
    setMsgBookingId(null);
    setMsgMode(null);
    if (wasCreated) onClose();
  };

  const venueColor = venues.find((v) => v.venue_id === form.venue_id)?.color || "#10B981";
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { category: "Food/Catering", label: "", amount: "", cost: "" }] }));
  const updItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const _ao = parseFloat(form.amount_online) || 0, _ac = parseFloat(form.amount_cash) || 0;
  const _adv = form.payment_mode === "split" ? _ao + _ac : (parseFloat(form.advance_amount) || 0);
  const _pending = Math.max(0, (parseFloat(form.amount) || 0) - _adv);

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-sm:!top-auto max-sm:!bottom-0 max-sm:!translate-y-0 max-sm:!max-w-full max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-h-[90vh] overflow-y-auto rounded-xl sm:max-w-md" data-testid="book-date-modal">
        {success ? (
          <div className="py-10 text-center" data-testid="booking-success">
            <motion.div className="mx-auto grid h-20 w-20 place-items-center rounded-full check-circle-anim" style={{ background: "#10B981" }}>
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path className="check-draw-anim" d="M10 21 L17 28 L30 13" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>
            <h3 className="mt-5 text-xl font-bold text-pulse-navy">Booked!</h3>
            <p className="mt-1 text-sm text-slate">Revenue updated automatically.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-pulse-navy">{editing ? "Edit booking" : "Book a date"}</DialogTitle>
            </DialogHeader>
            {editing && (
              <button type="button" data-testid="open-confirmation-msg"
                onClick={() => { setMsgMode("edit"); setMsgBookingId(editing.booking_id); }}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-md border border-pulse-green px-4 py-2.5 text-sm font-semibold text-pulse-green transition-colors hover:bg-pulse-green/10">
                <MessageCircle size={16} /> Confirmation message
              </button>
            )}
            <form onSubmit={submit} className="mt-1 space-y-4">
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-slate">Venue</label>
                <div className="mt-2 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-white" data-testid="modal-venue-locked"
                  style={{ background: venues.find((v) => v.venue_id === form.venue_id)?.color || "#10B981" }}>
                  <span className="h-2 w-2 rounded-full bg-white" />
                  {venues.find((v) => v.venue_id === form.venue_id)?.name || "Venue"}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-slate">Date</label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="modal-date-trigger"
                      className="mt-1.5 flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green">
                      <span className={form.date ? "text-charcoal" : "text-slate"}>{form.date ? niceDate(form.date) : "Pick a date"}</span>
                      <CalendarDays size={16} className="text-slate" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0" data-testid="modal-date-calendar">
                    <Calendar mode="single" selected={fromISO(form.date)} defaultMonth={fromISO(form.date)}
                      onSelect={(d) => { if (d) { setForm({ ...form, date: toISO(d) }); setDateOpen(false); } }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-slate">Payment received <span className="text-pulse-green">*</span></label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[["full", "Full payment"], ["advance", "Advance payment"]].map(([val, lbl]) => (
                    <button type="button" key={val} data-testid={`modal-ptype-${val}`}
                      onClick={() => setForm({ ...form, payment_type: val })}
                      className={`rounded-md border px-3 py-2 text-sm font-medium transition-all ${
                        form.payment_type === val ? "border-pulse-green bg-pulse-green text-white" : "border-slate-200 text-slate"
                      }`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[["online", "Online"], ["cash", "Cash"], ["split", "Split"]].map(([val, lbl]) => (
                    <button type="button" key={val} data-testid={`modal-payment-${val}`}
                      onClick={() => setForm({ ...form, payment_mode: val })}
                      className={`rounded-md border px-3 py-2 text-sm font-medium transition-all ${
                        form.payment_mode === val ? "border-pulse-navy bg-pulse-navy text-white" : "border-slate-200 text-slate"
                      }`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {form.payment_type === "advance" ? (
                <div className="space-y-3 rounded-md bg-cloud/60 p-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-slate">Total amount (₹) <span className="text-pulse-green">*</span></label>
                    <input type="number" inputMode="decimal" data-testid="modal-total-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                  </div>
                  {form.payment_mode === "split" ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate">Advance online (₹)</label>
                        <input type="number" inputMode="decimal" data-testid="modal-amount-online" value={form.amount_online} onChange={(e) => setForm({ ...form, amount_online: e.target.value })}
                          placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                      </div>
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate">Advance cash (₹)</label>
                        <input type="number" inputMode="decimal" data-testid="modal-amount-cash" value={form.amount_cash} onChange={(e) => setForm({ ...form, amount_cash: e.target.value })}
                          placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wide text-slate">Advance received (₹) <span className="text-pulse-green">*</span></label>
                      <input type="number" inputMode="decimal" data-testid="modal-advance-input" value={form.advance_amount} onChange={(e) => setForm({ ...form, advance_amount: e.target.value })}
                        placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm" data-testid="modal-pending-line">
                    <span className="text-slate">Pending after advance</span>
                    <b className="text-pulse-navy">₹{_pending.toLocaleString("en-IN")}</b>
                  </div>
                </div>
              ) : form.payment_mode === "split" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-slate">Online (₹) <span className="text-pulse-green">*</span></label>
                    <input type="number" inputMode="decimal" data-testid="modal-amount-online" value={form.amount_online} onChange={(e) => setForm({ ...form, amount_online: e.target.value })}
                      placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-slate">Cash (₹) <span className="text-pulse-green">*</span></label>
                    <input type="number" inputMode="decimal" data-testid="modal-amount-cash" value={form.amount_cash} onChange={(e) => setForm({ ...form, amount_cash: e.target.value })}
                      placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                  </div>
                  <div className="col-span-2 text-xs text-slate" data-testid="modal-split-total">
                    Total: <b className="text-pulse-navy">₹{(_ao + _ac).toLocaleString("en-IN")}</b>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-slate">Amount (₹) <span className="text-pulse-green">*</span></label>
                  <input type="number" inputMode="decimal" data-testid="modal-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-slate">Client name <span className="text-pulse-green">*</span></label>
                  <input data-testid="modal-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-slate">Client phone <span className="normal-case">(optional)</span></label>
                  <input type="tel" inputMode="tel" data-testid="modal-client-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                    className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-slate">Your name <span className="normal-case">(optional)</span></label>
                <input data-testid="modal-booker-input" value={form.booker_name} onChange={(e) => setForm({ ...form, booker_name: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-base outline-none focus:border-pulse-green" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium uppercase tracking-wide text-slate">Add-on items <span className="normal-case">(catering, DJ, decor…)</span></label>
                  <button type="button" onClick={addItem} data-testid="modal-add-item" className="text-xs font-semibold text-pulse-green">+ Add item</button>
                </div>
                {form.items.map((it, idx) => (
                  <div key={idx} className="mt-2 space-y-2 rounded-md border border-slate-200 p-2.5" data-testid={`modal-item-${idx}`}>
                    <div className="flex flex-wrap gap-1.5">
                      {ITEM_CATS.map((c) => (
                        <button type="button" key={c} data-testid={`modal-item-cat-${idx}-${c}`} onClick={() => updItem(idx, { category: c })}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-all ${it.category === c ? "border-pulse-navy bg-pulse-navy text-white" : "border-slate-200 text-slate"}`}>{c}</button>
                      ))}
                    </div>
                    <input data-testid={`modal-item-label-${idx}`} value={it.label} onChange={(e) => updItem(idx, { label: e.target.value })} placeholder="Detail (optional, e.g. 200 plates)"
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-base outline-none focus:border-pulse-green" />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" inputMode="decimal" data-testid={`modal-item-amount-${idx}`} value={it.amount} onChange={(e) => updItem(idx, { amount: e.target.value })} placeholder="Charge ₹"
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-base outline-none focus:border-pulse-green" />
                      <input type="number" inputMode="decimal" data-testid={`modal-item-cost-${idx}`} value={it.cost} onChange={(e) => updItem(idx, { cost: e.target.value })} placeholder="Your cost ₹ (opt)"
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-base outline-none focus:border-pulse-green" />
                    </div>
                    <button type="button" onClick={() => removeItem(idx)} data-testid={`modal-item-remove-${idx}`} className="text-xs font-medium text-red-500">Remove</button>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-slate">Note (optional)</label>
                <input data-testid="modal-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-pulse-green" />
              </div>

              <div className="flex items-center gap-2">
                {["confirmed", "tentative"].map((s) => (
                  <button type="button" key={s} onClick={() => setForm({ ...form, status: s })}
                    data-testid={`modal-status-${s}`}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium capitalize transition-all ${
                      form.status === s ? "border-pulse-navy bg-pulse-navy text-white" : "border-slate-200 text-slate"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>

              {!editing && (
                <label className="flex items-center gap-2.5 rounded-md bg-cloud px-3 py-2.5 text-sm text-charcoal">
                  <input type="checkbox" data-testid="modal-allow-multiple" checked={form.allow_multiple}
                    onChange={(e) => setForm({ ...form, allow_multiple: e.target.checked })}
                    className="h-4 w-4 accent-pulse-green" />
                  Add another booking this day (override single-per-day)
                </label>
              )}

              <div className="flex gap-2 pt-1">
                {editing && canDelete && (
                  <button type="button" onClick={del} data-testid="modal-cancel-booking"
                    className="grid place-items-center rounded-md border border-red-200 px-4 py-3 text-red-600 transition-colors hover:bg-red-50">
                    <Trash2 size={18} />
                  </button>
                )}
                <button type="submit" disabled={saving} data-testid="modal-save-booking"
                  className="flex flex-1 items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
                  style={{ background: venueColor }}>
                  <Check size={16} /> {saving ? "Saving…" : editing ? "Save changes" : "Confirm booking"}
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
    <ConfirmationMessageModal open={!!msgBookingId} bookingId={msgBookingId} businessId={businessId} onClose={closeMsg} />
    </>
  );
}
